import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { hasPermission } from '../utils/permissions';

/* ── Per-user sidebar list pinning ── */
function sidebarListsKey(userId) { return `crm_sidebar_lists_${userId}`; }
function loadPinnedListIds(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(sidebarListsKey(userId));
    if (raw !== null) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null; // null = not configured yet → show all
}
function savePinnedListIds(userId, ids) {
  if (!userId) return;
  try { localStorage.setItem(sidebarListsKey(userId), JSON.stringify(ids)); } catch { /* ignore */ }
}

/* ── SVG icon set for default nav categories ── */
const S = { w: 20, h: 20, vb: '0 0 24 24', fill: 'none', stroke: 'currentColor', sw: '1.8', lc: 'round', lj: 'round' };
const CAT_ICONS = {
  c_dash: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
      <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
    </svg>
  ),
  c_leads: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  c_daily: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <polyline points="9 11 12 14 22 4"/>
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
    </svg>
  ),
  c_sales: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
      <polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  c_tools: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <circle cx="12" cy="12" r="3"/>
      <path d="M6.7 6.7a7 7 0 100 10.6M17.3 6.7a7 7 0 010 10.6"/>
    </svg>
  ),
  c_system: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
};

/* ── SVG icon set for nav items ── */
const ITEM_ICONS = {
  tasks: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <polyline points="9 11 12 14 22 4"/>
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
    </svg>
  ),
  pipeline: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
      <polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  termin: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  angebote: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="2"/>
      <line x1="9" y1="12" x2="15" y2="12"/>
      <line x1="9" y1="16" x2="13" y2="16"/>
    </svg>
  ),
  calls: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.3 10.82 19.79 19.79 0 01.21 2.18 2 2 0 012.18 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.26 7.84a16 16 0 006.4 6.4l1.2-1.2a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
    </svg>
  ),
  contacts: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  companies: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  reports: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  maps: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  import: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  templates: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  automations: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  analytics: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <line x1="12" y1="20" x2="12" y2="10"/>
      <line x1="18" y1="20" x2="18" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="16"/>
    </svg>
  ),
  insights: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  trash: (
    <svg width={S.w} height={S.h} viewBox={S.vb} fill={S.fill} stroke={S.stroke} strokeWidth={S.sw} strokeLinecap={S.lc} strokeLinejoin={S.lj}>
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14H6L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
    </svg>
  ),
};

const NAV_KEY = 'crm_nav_v2';

const ITEM_PERMISSIONS = {
  import:      'contacts.import',
  reports:     'analytics.view',
  analytics:   'analytics.view',
  insights:    'analytics.view',
  trash:       'trash.view',
  automations: 'automations.manage',
};

const ITEM_POOL = [
  { id: 'tasks',       label: 'Aufgaben',           emoji: '✅' },
  { id: 'pipeline',    label: 'Pipeline',            emoji: '📈' },
  { id: 'termin',      label: 'Termine',             emoji: '📅' },
  { id: 'angebote',    label: 'Angebote',            emoji: '📋' },
  { id: 'calls',       label: 'Anrufe',              emoji: '📞' },
  { id: 'contacts',    label: 'Alle Kontakte',       emoji: '👤' },
  { id: 'companies',   label: 'Firmen',              emoji: '🏢' },
  { id: 'reports',     label: 'Berichte & Export',   emoji: '📊' },
  { id: 'maps',        label: 'Maps',                emoji: '🗺️' },
  { id: 'import',      label: 'Liste importieren',   emoji: '📥' },
  { id: 'templates',   label: 'Nachrichtenvorlagen', emoji: '📄' },
  { id: 'automations', label: 'Automationen',        emoji: '⚡' },
  { id: 'analytics',   label: 'Analytics',           emoji: '📊' },
  { id: 'insights',    label: 'Insights',            emoji: '🎯' },
  { id: 'trash',       label: 'Papierkorb',          emoji: '🗑️' },
];

const DEFAULT_CATS = [
  { id: 'c_dash',  emoji: '📊', label: 'Dashboard',         type: 'single', target: 'dashboard' },
  { id: 'c_leads', emoji: '👥', label: 'Listen & Kontakte',  type: 'leads' },
  { id: 'c_daily', emoji: '📋', label: 'Daily',              type: 'daily' },
  {
    id: 'c_sales', emoji: '💼', label: 'Sales', type: 'group',
    items: [
      { id: 'tasks',    label: 'Aufgaben',      emoji: '✅' },
      { id: 'pipeline', label: 'Pipeline',      emoji: '📈' },
      { id: 'termin',   label: 'Termine',       emoji: '📅' },
      { id: 'contacts',  label: 'Alle Kontakte',    emoji: '👤' },
      { id: 'companies', label: 'Firmen',           emoji: '🏢' },
      { id: 'angebote',  label: 'Angebote',         emoji: '📋' },
      { id: 'calls',     label: 'Anrufe',           emoji: '📞' },
      { id: 'reports',   label: 'Berichte & Export', emoji: '📊' },
    ],
  },
  {
    id: 'c_tools', emoji: '🛠️', label: 'Tools', type: 'group',
    items: [
      { id: 'maps',      label: 'Maps',                emoji: '🗺️' },
      { id: 'import',    label: 'Liste importieren',   emoji: '📥' },
      { id: 'templates', label: 'Nachrichtenvorlagen', emoji: '📄' },
    ],
  },
  {
    id: 'c_system', emoji: '⚙️', label: 'System', type: 'group',
    items: [
      { id: 'automations', label: 'Automationen', emoji: '⚡' },
      { id: 'analytics',   label: 'Analytics',    emoji: '📊' },
      { id: 'insights',    label: 'Insights',     emoji: '🎯' },
      { id: 'trash',       label: 'Papierkorb',   emoji: '🗑️' },
    ],
  },
];

function loadCats() {
  try {
    const s = localStorage.getItem(NAV_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return DEFAULT_CATS.map(c => ({ ...c, items: c.items ? [...c.items] : undefined }));
}

function saveCats(cats) {
  try { localStorage.setItem(NAV_KEY, JSON.stringify(cats)); } catch {}
}

// ── Daily content for flyout ──────────────────────────────────────────────────
function DailyContent({ tasks, onToggleTask, onOpenLead }) {
  const today = new Date().toISOString().slice(0, 10);
  const todayTasks = tasks.filter(t => t.dueDate === today);
  const pending = todayTasks.filter(t => !t.done).sort((a, b) => new Date(a.dueDatetime) - new Date(b.dueDatetime));
  const done    = todayTasks.filter(t =>  t.done).slice(-3);

  if (todayTasks.length === 0) {
    return (
      <div style={{ padding: '24px 12px', textAlign: 'center' }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 8 }}>
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>Keine Aufgaben heute</div>
      </div>
    );
  }
  return (
    <div>
      {pending.map(task => {
        const overdue = new Date(task.dueDatetime) < new Date();
        return (
          <div key={task.id}
            onClick={() => task.lead && onOpenLead(task.lead)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 10px', borderRadius: 7, cursor: task.lead ? 'pointer' : 'default', transition: 'background .1s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <button onClick={e => { e.stopPropagation(); onToggleTask(task.id); }} style={{
              width: 15, height: 15, borderRadius: 4, flexShrink: 0, marginTop: 2,
              border: `2px solid ${overdue ? '#dc2626' : 'var(--accent)'}`,
              background: 'transparent', cursor: 'pointer',
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: overdue ? '#dc2626' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
              {task.lead && <div style={{ fontSize: 10, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.lead.name}</div>}
            </div>
            <span style={{ fontSize: 10, whiteSpace: 'nowrap', flexShrink: 0, color: overdue ? '#dc2626' : 'var(--accent)', fontWeight: overdue ? 600 : 400, marginTop: 2 }}>{task.dueTime}</span>
          </div>
        );
      })}
      {done.map(task => (
        <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', opacity: 0.45 }}>
          <button onClick={e => { e.stopPropagation(); onToggleTask(task.id); }} style={{
            width: 15, height: 15, borderRadius: 4, flexShrink: 0,
            border: '2px solid var(--text3)', background: 'var(--text3)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
          </button>
          <div style={{ fontSize: 11, color: 'var(--text3)', textDecoration: 'line-through', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{task.title}</div>
        </div>
      ))}
    </div>
  );
}

// ── Flyout (navigation) ───────────────────────────────────────────────────────
function Flyout({ cat, items, activeSection, activeList, onItemClick, onEnter, onLeave, top, tasks, onToggleTask, onOpenLead, onAddList }) {
  const viewH = window.innerHeight;
  const estH  = cat.type === 'daily' ? 300 : Math.min(64 + items.length * 44 + (cat.type === 'leads' ? 48 : 0), 520);
  const finalTop = Math.max(Math.min(top - 4, viewH - estH - 20), 8);

  return (
    <div
      className="gl-flyout"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        position: 'fixed', left: 64, top: finalTop,
        width: 264, maxHeight: 540,
        borderRadius: '0 20px 20px 0',
        borderLeft: 'none',
        zIndex: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
    >

      {/* Header — small caps label only, no emoji clutter */}
      <div style={{
        padding: '14px 20px 12px',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: 'var(--text3)',
        }}>{cat.label}</span>
      </div>

      {/* Items — spacious, clean, no per-item borders */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
        {cat.type === 'daily' && (
          <DailyContent tasks={tasks} onToggleTask={onToggleTask} onOpenLead={onOpenLead} />
        )}
        {(cat.type === 'group' || cat.type === 'leads') && items.map(item => {
          const isActive = cat.type === 'leads'
            ? (activeSection === 'contacts' || activeSection === 'leads') && ('list_' + activeList) === item.id
            : activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onItemClick(item.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center',
                padding: '11px 20px',
                background: isActive ? 'var(--accent-bg)' : 'transparent',
                color: isActive ? 'var(--accent)' : 'var(--text)',
                boxShadow: isActive ? 'inset 3px 0 0 var(--accent)' : 'none',
                border: 'none',
                fontSize: 14, fontWeight: isActive ? 600 : 400,
                cursor: 'pointer', textAlign: 'left',
                transition: 'background .1s, color .1s, box-shadow .1s',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg3)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
              {item.count > 0 && (
                <span style={{
                  fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 600,
                  color: isActive ? 'var(--accent)' : 'var(--text3)',
                  background: 'var(--bg4)',
                  borderRadius: 10, padding: '1px 7px', flexShrink: 0,
                  marginLeft: 8,
                }}>{item.count}</span>
              )}
            </button>
          );
        })}

        {/* "+ Ansicht hinzufügen" for leads flyout */}
        {cat.type === 'leads' && onAddList && (
          <button
            onClick={onAddList}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 20px',
              background: 'transparent', border: 'none',
              borderTop: '1px solid var(--border)',
              fontSize: 12, fontWeight: 600, color: 'var(--accent)',
              cursor: 'pointer', textAlign: 'left',
              transition: 'background .1s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Ansicht / Liste hinzufügen
          </button>
        )}
      </div>
    </div>
  );
}

// ── Edit flyout ───────────────────────────────────────────────────────────────
function EditFlyout({ cat, top, onClose, onUpdate, lists, onUpdateLists, counts }) {
  const [label,      setLabel]      = useState(cat.label);
  const [emoji,      setEmoji]      = useState(cat.emoji);
  const [items,      setItems]      = useState(cat.items ? [...cat.items] : []);
  const [dragId,     setDragId]     = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [addingItem, setAddingItem] = useState(false);

  const viewH    = window.innerHeight;
  const finalTop = Math.max(Math.min(top, viewH - 480 - 10), 10);
  const pool     = ITEM_POOL.filter(p => !items.some(i => i.id === p.id));

  function save() {
    onUpdate({ ...cat, label, emoji, items });
    onClose();
  }

  function dropItem(targetId) {
    if (!dragId || dragId === targetId) return;
    const from = items.findIndex(i => i.id === dragId);
    const to   = items.findIndex(i => i.id === targetId);
    const arr  = [...items];
    const [m]  = arr.splice(from, 1);
    arr.splice(to, 0, m);
    setItems(arr);
    setDragId(null); setDragOverId(null);
  }

  return (
    <div className="gl-flyout" style={{
      position: 'fixed', left: 64, top: finalTop,
      width: 264, maxHeight: 500,
      borderRadius: '0 20px 20px 0',
      borderLeft: '2px solid var(--accent)',
      zIndex: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Bearbeiten</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>

        {/* Name + emoji row */}
        {cat.type !== 'leads' && cat.type !== 'daily' && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Name & Symbol</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={emoji} onChange={e => setEmoji(e.target.value)}
                style={{ width: 46, height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 20, textAlign: 'center', outline: 'none' }} />
              <input value={label} onChange={e => setLabel(e.target.value)}
                style={{ flex: 1, height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, padding: '0 10px', outline: 'none' }} />
            </div>
          </div>
        )}

        {/* Group items editor */}
        {cat.type === 'group' && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Einträge</div>
            {items.map(item => {
              const isDrag = dragId === item.id;
              const isOver = dragOverId === item.id;
              return (
                <div key={item.id}
                  draggable
                  onDragStart={() => setDragId(item.id)}
                  onDragOver={e => { e.preventDefault(); setDragOverId(item.id); }}
                  onDragLeave={() => setDragOverId(id => id === item.id ? null : id)}
                  onDrop={e => { e.preventDefault(); dropItem(item.id); }}
                  onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
                    borderRadius: 7, marginBottom: 2, cursor: 'grab',
                    background: isOver ? 'var(--accent-bg)' : 'var(--bg3)',
                    border: `1px solid ${isOver ? 'var(--accent-border)' : 'var(--border)'}`,
                    opacity: isDrag ? 0.4 : 1, transition: 'background .1s, border .1s',
                  }}
                >
                  <svg width="9" height="11" viewBox="0 0 9 11" fill="var(--text3)">
                    <circle cx="2.5" cy="2" r="1.1"/><circle cx="6.5" cy="2" r="1.1"/>
                    <circle cx="2.5" cy="5.5" r="1.1"/><circle cx="6.5" cy="5.5" r="1.1"/>
                    <circle cx="2.5" cy="9" r="1.1"/><circle cx="6.5" cy="9" r="1.1"/>
                  </svg>
                  <span style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)', flexShrink: 0 }}>{ITEM_ICONS[item.id] ?? <span style={{ fontSize: 13 }}>{item.emoji}</span>}</span>
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                  <button onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              );
            })}

            {pool.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {addingItem ? (
                  <>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>Aus Pool hinzufügen:</div>
                    {pool.map(p => (
                      <button key={p.id}
                        onClick={() => { setItems(prev => [...prev, p]); setAddingItem(false); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px', borderRadius: 7, marginBottom: 2, width: '100%', background: 'var(--bg3)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12, color: 'var(--text)', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--bg3)'}
                      >
                        <span style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)', flexShrink: 0 }}>{ITEM_ICONS[p.id] ?? <span style={{ fontSize: 13 }}>{p.emoji}</span>}</span>
                        <span>{p.label}</span>
                      </button>
                    ))}
                    <button onClick={() => setAddingItem(false)} style={{ marginTop: 4, fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer' }}>Abbrechen</button>
                  </>
                ) : (
                  <button onClick={() => setAddingItem(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 7, width: '100%', background: 'transparent', border: '1.5px dashed var(--border2)', cursor: 'pointer', fontSize: 12, color: 'var(--text3)', justifyContent: 'center' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Eintrag hinzufügen
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Leads: manage lists visibility */}
        {cat.type === 'leads' && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Listen verwalten</div>
            {lists.map(list => (
              <div key={list.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 7, marginBottom: 2, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
                <span style={{ flex: 1, fontSize: 12, color: list.hidden ? 'var(--text3)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.label}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)', background: 'var(--bg4)', borderRadius: 10, padding: '1px 6px', border: '1px solid var(--border)' }}>{counts[list.id] || 0}</span>
                <button
                  onClick={() => onUpdateLists(lists.map(l => l.id === list.id ? { ...l, hidden: !l.hidden } : l))}
                  title={list.hidden ? 'Einblenden' : 'Ausblenden'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: list.hidden ? 'var(--text3)' : 'var(--text2)', padding: 2, display: 'flex', alignItems: 'center' }}>
                  {list.hidden
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22"/></svg>
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {cat.type === 'group' && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <button onClick={save}
            style={{ width: '100%', height: 34, borderRadius: 8, background: 'var(--accent)', border: 'none', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Speichern
          </button>
        </div>
      )}
    </div>
  );
}

// ── User mark ─────────────────────────────────────────────────────────────────
function UserMark({ currentUser, onLogout }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const flyoutRef = useRef(null);
  const triggerRef = useRef(null);
  const name = currentUser?.name || currentUser?.email || 'User';
  const role = currentUser?.role || 'viewer';
  const title = currentUser?.title || '';
  const avatar = currentUser?.avatar || '';
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const [menuPos, setMenuPos] = useState({ left: 70, bottom: 16 });

  useEffect(() => {
    function handleOutside(e) {
      const t = e.target;
      if (wrapperRef.current?.contains(t) || flyoutRef.current?.contains(t)) return;
      setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('touchstart', handleOutside, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPos({
        left: Math.min(rect.right + 10, window.innerWidth - 264 - 12),
        bottom: Math.max(10, window.innerHeight - rect.bottom),
      });
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(v => !v)}
        title={`${name} – ${role}`}
        aria-label={`Benutzerprofil: ${name}`}
        aria-expanded={open}
        style={{
          width: 40, height: 40, borderRadius: 11, marginBottom: 4,
          background: open ? 'var(--accent-bg)' : 'transparent',
          border: open ? '1.5px solid var(--accent-border)' : '1.5px solid transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: open ? '0 2px 8px rgba(29,190,116,0.2)' : 'none',
          padding: 0,
        }}
      >
        {avatar ? (
          <img src={avatar} alt={name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--accent-border)' }} />
        ) : (
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), #0f8a53)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white' }}>
            {initials}
          </div>
        )}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={flyoutRef}
          className="gl-flyout"
          style={{ position: 'fixed', left: menuPos.left, bottom: menuPos.bottom, width: 260, borderRadius: 16, padding: 12, zIndex: 900 }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
            {avatar ? (
              <img src={avatar} alt={name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border)' }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-bg)', border: '1.5px solid var(--accent-border)', color: 'var(--accent)', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials}</div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUser?.email || ''}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', color: 'var(--accent)', fontWeight: 600 }}>{role}</span>
            {title && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)' }}>{title}</span>}
          </div>
          <button
            type="button"
            onClick={onLogout}
            style={{ width: '100%', height: 34, borderRadius: 8, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 12, fontWeight: 700 }}
          >
            Abmelden
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ── List Picker Modal ─────────────────────────────────────────────────────────
function ListPickerModal({ lists, pinnedIds, onSave, onClose, onCreateList, counts }) {
  const initialSelected = pinnedIds !== null
    ? new Set(pinnedIds)
    : new Set(lists.filter(l => l.id !== 'all').map(l => l.id));
  const [selected, setSelected] = useState(initialSelected);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    const newId = onCreateList(name);
    if (newId) setSelected(prev => new Set([...prev, newId]));
    setNewName('');
    setCreating(false);
  }

  function handleSave() {
    onSave([...selected]);
    onClose();
  }

  const editableLists = lists.filter(l => l.id !== 'all');

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(8,12,24,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--bg2)', borderRadius: 18, border: '1px solid var(--border)', width: 'min(460px, 100%)', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)' }}>Ansichten / Listen</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>Wähle welche Listen du in der Sidebar sehen möchtest</div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {/* "Alle Kontakte" is always shown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', opacity: 0.55 }}>
            <div style={{ width: 20, height: 20, borderRadius: 6, border: '1.5px solid var(--accent)', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Alle Kontakte</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Immer sichtbar · {counts['all'] || 0} Kontakte</div>
            </div>
          </div>

          {editableLists.length === 0 && !creating && (
            <div style={{ textAlign: 'center', padding: '24px 20px', color: 'var(--text3)', fontSize: 13 }}>
              Noch keine Listen vorhanden. Erstelle eine neue Liste.
            </div>
          )}

          {editableLists.map(l => {
            const on = selected.has(l.id);
            const cnt = counts[l.id] || 0;
            return (
              <button key={l.id} type="button" onClick={() => toggle(l.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .12s' }}>
                  {on && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: on ? 600 : 400, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{cnt} Kontakte</div>
                </div>
              </button>
            );
          })}

          {/* New list form */}
          {creating ? (
            <div style={{ padding: '10px 20px', display: 'flex', gap: 8 }}>
              <input
                autoFocus
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
                placeholder="Name der neuen Liste..."
                style={{ flex: 1, height: 36, borderRadius: 8, border: '1.5px solid var(--accent-border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, padding: '0 12px', outline: 'none', fontFamily: 'inherit' }}
              />
              <button type="button" onClick={handleCreate} style={{ height: 36, padding: '0 14px', borderRadius: 8, background: 'var(--accent)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Erstellen</button>
              <button type="button" onClick={() => setCreating(false)} style={{ height: 36, padding: '0 12px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>✕</button>
            </div>
          ) : (
            <button type="button" onClick={() => setCreating(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent)', fontSize: 13, fontWeight: 600 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Neue Liste erstellen
            </button>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" onClick={() => setSelected(new Set(editableLists.map(l => l.id)))}
            style={{ fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6 }}>Alle</button>
          <button type="button" onClick={() => setSelected(new Set())}
            style={{ fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6 }}>Keine</button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={{ height: 36, padding: '0 16px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 13, color: 'var(--text2)' }}>Abbrechen</button>
          <button type="button" onClick={handleSave} style={{ height: 36, padding: '0 20px', borderRadius: 8, background: 'var(--accent)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Speichern</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Main Sidebar ──────────────────────────────────────────────────────────────
export default function Sidebar({
  lists, onUpdateLists,
  activeSection, setActiveSection,
  activeList, setActiveList,
  counts, tasks = [], onToggleTask, onOpenLead,
  currentUser, onLogout,
  unreadMessages = 0, onOpenInbox,
  onlineUsers = [], authUsers = [],
}) {
  const [cats,           setCats]           = useState(loadCats);
  const [openId,         setOpenId]         = useState(null);
  const [editMode,       setEditMode]       = useState(false);
  const [editingCatId,   setEditingCatId]   = useState(null);
  const [showAddCat,     setShowAddCat]     = useState(false);
  const [newCat,         setNewCat]         = useState({ emoji: '⭐', label: '' });
  const [dragId,         setDragId]         = useState(null);
  const [dragOverId,     setDragOverId]     = useState(null);
  const [pinnedListIds,  setPinnedListIds]  = useState(() => loadPinnedListIds(currentUser?.id));
  const [showListPicker, setShowListPicker] = useState(false);

  // Reload pinned list IDs when user changes
  useEffect(() => {
    setPinnedListIds(loadPinnedListIds(currentUser?.id));
  }, [currentUser?.id]);

  const btnRefs   = useRef({});
  const closeTimer = useRef(null);

  useEffect(() => { saveCats(cats); }, [cats]);

  function isCatActive(cat) {
    if (cat.type === 'single') return activeSection === cat.target;
    if (cat.type === 'leads')  return activeSection === 'contacts' || activeSection === 'leads';
    if (cat.type === 'group')  return (cat.items || []).some(i => activeSection === i.id);
    return false;
  }

  function getBtnTop(id) {
    const el = btnRefs.current[id];
    if (!el) return 80;
    return el.getBoundingClientRect().top;
  }

  function openFlyout(id) { clearTimeout(closeTimer.current); setOpenId(id); }
  function startClose()    { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpenId(null), 200); }

  function handleCatClick(cat) {
    if (editMode) {
      const newId = editingCatId === cat.id ? null : cat.id;
      setEditingCatId(newId);
      if (newId) openFlyout(newId); else setOpenId(null);
      return;
    }
    if (cat.type === 'single') {
      setActiveSection(cat.target);
      setOpenId(null);
    } else {
      setOpenId(openId === cat.id ? null : cat.id);
    }
  }

  function handleItemNav(catType, itemId) {
    if (catType === 'leads') {
      setActiveList(itemId.replace(/^list_/, ''));
      setActiveSection('contacts');
    } else {
      setActiveSection(itemId);
    }
    setOpenId(null);
  }

  function getFlyoutItems(cat) {
    if (cat.type === 'group') {
      return (cat.items || []).filter(item => {
        const perm = ITEM_PERMISSIONS[item.id];
        return !perm || hasPermission(currentUser, perm);
      });
    }
    if (cat.type === 'leads') {
      const visible = pinnedListIds !== null
        ? lists.filter(l => !l.hidden && (l.id === 'all' || pinnedListIds.includes(l.id)))
        : lists.filter(l => !l.hidden);
      return visible.map(l => ({
        id: 'list_' + l.id, label: l.label, emoji: '📋', count: counts[l.id] || 0,
      }));
    }
    return [];
  }

  function dropCat(targetId) {
    if (!dragId || dragId === targetId) return;
    const from = cats.findIndex(c => c.id === dragId);
    const to   = cats.findIndex(c => c.id === targetId);
    const arr  = [...cats];
    const [m]  = arr.splice(from, 1);
    arr.splice(to, 0, m);
    setCats(arr);
    setDragId(null); setDragOverId(null);
  }

  const nowMs        = Date.now();
  const todayStr     = new Date().toISOString().slice(0, 10);
  const pendingToday = tasks.filter(t => !t.done && t.dueDate === todayStr).length;
  const pendingAll   = tasks.filter(t => !t.done).length;
  const overdueCount = tasks.filter(t => !t.done && (t.dueDatetime ? new Date(t.dueDatetime).getTime() : t.dueDate ? new Date(t.dueDate + 'T23:59').getTime() : null) < nowMs).length;
  const openCat      = cats.find(c => c.id === openId);

  return (
    <>
      {/* ── Icon Rail ── */}
      <aside className="gl-sidebar" style={{
        width: 64, minWidth: 64,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '12px 0 10px',
        zIndex: 30, position: 'relative',
        overflowY: 'auto', overflowX: 'visible',
        gap: 2,
      }}>

        {cats.map(cat => {
          const active  = isCatActive(cat);
          const isOpen  = openId === cat.id;
          const isDrag  = editMode && dragId === cat.id;
          const isOver  = editMode && dragOverId === cat.id;

          const isTaskCat = cat.type === 'single' && cat.target === 'tasks'
            || cat.type === 'group' && cat.items?.some(i => i.id === 'tasks');
          const badge = cat.type === 'daily' ? pendingToday : isTaskCat ? pendingAll : 0;
          const badgeOverdue = isTaskCat && overdueCount > 0;

          return (
            <div
              key={cat.id}
              ref={el => btnRefs.current[cat.id] = el}
              draggable={editMode}
              onDragStart={() => setDragId(cat.id)}
              onDragOver={e => { e.preventDefault(); setDragOverId(cat.id); }}
              onDragLeave={() => setDragOverId(id => id === cat.id ? null : id)}
              onDrop={e => { e.preventDefault(); dropCat(cat.id); }}
              onDragEnd={() => { setDragId(null); setDragOverId(null); }}
              onMouseEnter={() => !editMode && cat.type !== 'single' && openFlyout(cat.id)}
              onMouseLeave={() => !editMode && startClose()}
              style={{
                marginBottom: 4, position: 'relative',
                opacity: isDrag ? 0.35 : 1,
                borderRadius: 12,
                outline: isOver ? '2px dashed var(--accent)' : 'none',
                outlineOffset: 2,
              }}
            >
              <button
                onClick={() => handleCatClick(cat)}
                title={cat.label}
                style={{
                  width: 44, height: 44, borderRadius: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: editMode ? 'grab' : 'pointer',
                  color: active || isOpen ? 'var(--accent)' : 'var(--text2)',
                  background: active
                    ? 'rgba(29,190,116,0.13)'
                    : isOpen ? 'var(--bg3)' : 'transparent',
                  border: active
                    ? '1px solid rgba(29,190,116,0.28)'
                    : '1px solid transparent',
                  boxShadow: active
                    ? '0 2px 12px rgba(29,190,116,0.18), inset 0 1px 0 rgba(255,255,255,0.6)'
                    : 'none',
                  transition: 'all .14s var(--motion-ease)', position: 'relative',
                  outline: 'none',
                }}
                onMouseEnter={e => {
                  if (!active) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.38)';
                    e.currentTarget.style.color = 'var(--text)';
                    e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.7)';
                  }
                }}
                onMouseLeave={e => {
                  if (!active && !isOpen) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text2)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {CAT_ICONS[cat.id] ?? <span style={{ fontSize: 17, lineHeight: 1 }}>{cat.emoji}</span>}
                {badge > 0 && (
                  <span style={{
                    position: 'absolute', top: 4, right: 4,
                    minWidth: 15, height: 15, borderRadius: 8,
                    background: badgeOverdue ? '#dc2626' : (active ? 'white' : 'var(--accent)'),
                    color: badgeOverdue ? 'white' : (active ? 'var(--accent)' : 'white'),
                    fontSize: 8, fontWeight: 700, lineHeight: '15px',
                    padding: '0 3px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid var(--bg2)', boxSizing: 'content-box',
                  }}>{badge > 9 ? '9+' : badge}</span>
                )}
              </button>

              {editMode && (
                <button
                  onClick={e => { e.stopPropagation(); setCats(p => p.filter(c => c.id !== cat.id)); if (openId === cat.id) setOpenId(null); if (editingCatId === cat.id) setEditingCatId(null); }}
                  style={{
                    position: 'absolute', top: -3, right: -3,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#dc2626', color: 'white', border: '2px solid var(--bg2)',
                    fontSize: 10, cursor: 'pointer', lineHeight: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 5, padding: 0,
                  }}
                >×</button>
              )}
            </div>
          );
        })}

        {editMode && (
          <button
            onClick={() => { setShowAddCat(true); setNewCat({ emoji: '⭐', label: '' }); }}
            title="Neue Kategorie hinzufügen"
            style={{
              width: 44, height: 44, borderRadius: 12, marginBottom: 4, fontSize: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', background: 'transparent',
              border: '1.5px dashed var(--border2)', color: 'var(--text3)',
              transition: 'all .12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-bg)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.background = 'transparent'; }}
          >＋</button>
        )}

        <div style={{ flex: 1 }} />
        <div style={{ width: 28, height: 1, background: 'var(--border)', margin: '6px 0' }} />

        {/* Inbox button */}
        <div style={{ position: 'relative', marginBottom: 4 }}>
          <button
            onClick={onOpenInbox}
            title="Nachrichten"
            aria-label="Nachrichten"
            style={{
              width: 44, height: 44, borderRadius: 12, fontSize: 18,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', background: 'transparent',
              border: '1.5px solid transparent', color: 'var(--text3)',
              transition: 'all .12s', position: 'relative',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text3)'; }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
          </button>
          {unreadMessages > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 4,
              minWidth: 15, height: 15, borderRadius: 8,
              background: '#dc2626', color: 'white',
              fontSize: 8, fontWeight: 700, lineHeight: '15px',
              padding: '0 3px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid var(--bg2)', boxSizing: 'content-box',
              pointerEvents: 'none',
            }}>{unreadMessages > 9 ? '9+' : unreadMessages}</span>
          )}
        </div>

        {/* Edit toggle */}
        <button
          onClick={() => { setEditMode(v => !v); setOpenId(null); setEditingCatId(null); }}
          title={editMode ? 'Fertig' : 'Navigation anpassen'}
          aria-label={editMode ? 'Fertig' : 'Navigation anpassen'}
          style={{
            width: 44, height: 44, borderRadius: 12, marginBottom: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            background: editMode ? 'var(--accent-bg)' : 'transparent',
            border: editMode ? '1.5px solid var(--accent-border)' : '1.5px solid transparent',
            color: editMode ? 'var(--accent)' : 'var(--text3)',
            transition: 'all .12s',
          }}
          onMouseEnter={e => { if (!editMode) { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text2)'; } }}
          onMouseLeave={e => { if (!editMode) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text3)'; } }}
        >
          {editMode
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
            : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          }
        </button>

        {/* Online presence dots */}
        {onlineUsers.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', paddingBottom: 6 }}>
            {onlineUsers.filter(u => u.user_id !== currentUser?.id).slice(0, 3).map(u => {
              const user = authUsers.find(a => a.id === u.user_id);
              const initials = (u.name || user?.name || '?').slice(0, 1).toUpperCase();
              return (
                <div key={u.user_id} title={`${u.name || user?.name || 'Online'} – online`}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(22,163,74,0.15)', border: '2px solid #16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#16a34a', position: 'relative' }}>
                  {user?.avatar ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : initials}
                  <span style={{ position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderRadius: '50%', background: '#16a34a', border: '1.5px solid var(--bg)' }} />
                </div>
              );
            })}
          </div>
        )}
        <UserMark currentUser={currentUser} onLogout={onLogout} />
      </aside>

      {/* ── Navigation flyout ── */}
      {!editMode && openCat && (
        <Flyout
          cat={openCat}
          items={getFlyoutItems(openCat)}
          activeSection={activeSection}
          activeList={activeList}
          onItemClick={id => handleItemNav(openCat.type, id)}
          onEnter={() => clearTimeout(closeTimer.current)}
          onLeave={startClose}
          top={getBtnTop(openId)}
          tasks={tasks}
          onToggleTask={onToggleTask}
          onOpenLead={onOpenLead}
          onAddList={openCat.type === 'leads' ? () => { setOpenId(null); setShowListPicker(true); } : null}
        />
      )}

      {/* ── List Picker Modal ── */}
      {showListPicker && (
        <ListPickerModal
          lists={lists}
          pinnedIds={pinnedListIds}
          counts={counts}
          onSave={ids => {
            setPinnedListIds(ids);
            savePinnedListIds(currentUser?.id, ids);
          }}
          onClose={() => setShowListPicker(false)}
          onCreateList={name => {
            const id = 'list_' + Date.now();
            const newList = { id, label: name, hidden: false, createdBy: currentUser?.id };
            onUpdateLists(prev => [...prev, newList]);
            return id;
          }}
        />
      )}

      {/* ── Edit flyout ── */}
      {editMode && editingCatId && (() => {
        const ec = cats.find(c => c.id === editingCatId);
        if (!ec) return null;
        return (
          <EditFlyout
            cat={ec}
            top={getBtnTop(editingCatId)}
            onClose={() => { setEditingCatId(null); setOpenId(null); }}
            onUpdate={updated => setCats(prev => prev.map(c => c.id === editingCatId ? updated : c))}
            lists={lists}
            onUpdateLists={onUpdateLists}
            counts={counts}
          />
        );
      })()}

      {/* ── Add category modal ── */}
      {showAddCat && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowAddCat(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: '22px 24px', width: 320, border: '1px solid var(--border)', boxShadow: '0 20px 50px rgba(15,23,42,0.2)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>Neue Kategorie</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <input
                value={newCat.emoji}
                onChange={e => setNewCat(p => ({ ...p, emoji: e.target.value }))}
                placeholder="Emoji"
                style={{ width: 56, height: 40, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 22, textAlign: 'center', outline: 'none' }}
              />
              <input
                autoFocus
                value={newCat.label}
                onChange={e => setNewCat(p => ({ ...p, label: e.target.value }))}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newCat.label.trim()) {
                    setCats(prev => [...prev, { id: 'cat_' + Date.now(), ...newCat, type: 'group', items: [] }]);
                    setShowAddCat(false);
                  }
                  if (e.key === 'Escape') setShowAddCat(false);
                }}
                placeholder="Kategorie-Name"
                style={{ flex: 1, height: 40, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, padding: '0 12px', outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowAddCat(false)}
                style={{ flex: 1, height: 36, borderRadius: 8, background: 'var(--bg4)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>
                Abbrechen
              </button>
              <button
                disabled={!newCat.label.trim()}
                onClick={() => {
                  if (!newCat.label.trim()) return;
                  setCats(prev => [...prev, { id: 'cat_' + Date.now(), ...newCat, type: 'group', items: [] }]);
                  setShowAddCat(false);
                }}
                style={{ flex: 2, height: 36, borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: newCat.label.trim() ? 'pointer' : 'default', background: newCat.label.trim() ? 'var(--accent)' : 'var(--bg4)', color: newCat.label.trim() ? 'white' : 'var(--text3)' }}>
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
